import { APP_TIMEZONE } from '@/constants/timezone';
import {
  BMT_COURSE_AUDIENCE,
  BMT_ENROLMENT_STATUS,
  BMT_HOMEWORK_KIND,
  BMT_MAKE_UP_STATUS,
} from '@/constants/bmt';

/**
 * Display helpers for BMT Companion. Pure functions of the API's views, so the
 * screens hold no date or status logic of their own and every branch is testable.
 *
 * Times arrive as UTC instants and are always shown in Singapore time, whatever
 * zone the phone is in: a trainee abroad still has to be in the hall at 7 pm SGT.
 * `date` fields are already Singapore calendar dates (YYYY-MM-DD).
 */

const partsOf = (date, options) =>
  Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', options)
      .formatToParts(date)
      .filter(part => part.type !== 'literal')
      .map(part => [part.type, part.value]),
  );

const isValidDate = date => date instanceof Date && !Number.isNaN(date.getTime());

const parseCalendarDate = value => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  // Noon UTC, formatted in UTC: the calendar date cannot drift a day either way.
  const date = new Date(`${value}T12:00:00Z`);
  return isValidDate(date) ? date : null;
};

/** '2026-11-07' → 'Sat 7 Nov'. */
export function formatDayDate(value) {
  const date = parseCalendarDate(value);
  if (!date) return '';
  const { weekday, day, month } = partsOf(date, {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  return `${weekday} ${day} ${month}`;
}

/** '2026-11-05' → { month: 'NOV', day: '5', weekday: 'THU' }, for a calendar tile. */
export function calendarParts(value) {
  const date = parseCalendarDate(value);
  if (!date) return { month: '', day: '', weekday: '' };
  const { weekday, day, month } = partsOf(date, {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  return { month: month.toUpperCase(), day, weekday: weekday.toUpperCase() };
}

/** A UTC instant → '11am' or '4.20pm' in Singapore time. */
export function formatTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (!isValidDate(date)) return '';
  const { hour, minute, dayPeriod } = partsOf(date, {
    timeZone: APP_TIMEZONE,
    hour: 'numeric',
    minute: '2-digit',
    // h12, not hour12: some ICU builds read hour12 as h11 and print noon as "0pm".
    hourCycle: 'h12',
  });
  const clock = minute === '00' ? hour : `${hour}.${minute}`;
  return `${clock}${(dayPeriod || '').toLowerCase()}`;
}

/** '7pm – 9.30pm', or just the start when there is no end. */
export function formatTimeRange(startsAt, endsAt) {
  const start = formatTime(startsAt);
  const end = formatTime(endsAt);
  if (start && end) return `${start} – ${end}`;
  return start || end;
}

/** The first and last date of a run of days: 'Sat 7 Nov – Sat 28 Nov'. */
export function formatDateSpan(days = []) {
  const dates = days.map(day => day.date).filter(Boolean);
  if (!dates.length) return '';
  const first = formatDayDate(dates[0]);
  const last = formatDayDate(dates[dates.length - 1]);
  return first === last ? first : `${first} – ${last}`;
}

/**
 * The check-in line for a day. The app never offers self check-in: the only
 * way in is scanning the QR shown in the hall, so `canScan` is just a hint for
 * which button to emphasise.
 */
export function checkinLabel(checkin) {
  const state = checkin?.state || 'none';
  if (state === 'not-yet') {
    const at = formatTime(checkin.opensAt);
    return {
      state,
      text: at ? `Check-in opens at ${at}` : 'Check-in not open yet',
      canScan: false,
    };
  }
  if (state === 'open') return { state, text: 'Check-in open', canScan: true };
  if (state === 'closed') return { state, text: 'Check-in closed', canScan: false };
  return { state: 'none', text: '', canScan: false };
}

/**
 * One status for a whole day: upcoming while any session is still to come,
 * attended when every session is, partial when some were missed, missed when
 * none were attended.
 */
export function dayStatus(day) {
  const sessions = day?.sessions || [];
  if (!sessions.length || sessions.some(s => s.status === 'upcoming')) return 'upcoming';
  const attended = sessions.filter(s => s.status === 'attended').length;
  if (attended === sessions.length) return 'attended';
  return attended ? 'partial' : 'missed';
}

/**
 * The day a trainee should look at next: today's while check-in is not over,
 * otherwise the first one still to come. Null once every day has passed.
 */
export function nextDay(days = [], serverTime) {
  const now = serverTime ? new Date(serverTime) : new Date();
  return (
    days.find(day => {
      if (day.checkin?.state === 'open' || day.checkin?.state === 'not-yet') return true;
      if (day.checkin?.state === 'closed') return false;
      const end = day.endsAt ? new Date(day.endsAt) : null;
      if (end && isValidDate(end)) return end > now;
      return dayStatus(day) === 'upcoming';
    }) || null
  );
}

/** The Singapore calendar date of a UTC instant (or now), as YYYY-MM-DD. */
export function sgCalendarDate(value) {
  const date = value ? new Date(value) : new Date();
  if (!isValidDate(date)) return '';
  const { year, month, day } = partsOf(date, {
    timeZone: APP_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return `${year}-${month}-${day}`;
}

/** A day's topic titles, joined: 'Prayer · Serving in Church'. */
export const topicsText = day => (day?.sessions || []).map(s => s.title).join(' · ');

/** Where a day is: its venue, or 'Online' for a Zoom day. */
export const dayPlace = day => (day?.online ? 'Online' : day?.venue || '');

/** Time range and place on one line: '7pm – 9.30pm · Hall 2'. */
export const dayTimeVenue = day =>
  [formatTimeRange(day?.startsAt, day?.endsAt), dayPlace(day)].filter(Boolean).join(' · ');

/** Today's day (Singapore date, by the server's clock) while its check-in is open, or null. */
export function openCheckinDay(me) {
  if (!['pending', 'approved'].includes(bmtPhase(me))) return null;
  const today = sgCalendarDate(me.serverTime);
  return (me.days || []).find(day => day.date === today && day.checkin?.state === 'open') || null;
}

/**
 * What the /bmt/check-in screen says for a scan response kept in the store
 * (`bmt.lastCheckIn`: `{ status, code, display, checkIn, opensAt }`). A repeat
 * scan reads the same as the first: the attendance is recorded either way.
 */
export function checkInResultModel(result) {
  const checkIn = result?.checkIn || {};
  if (result?.status === 'success') {
    if (checkIn.enrolmentStatus === BMT_ENROLMENT_STATUS.WITHDRAWN) return withdrawnModel();
    const count = checkIn.sessions?.length || 0;
    return {
      kind: 'checked-in',
      title: "You're checked in",
      subtitle: checkIn.date ? `for ${formatDayDate(checkIn.date)}` : '',
      message:
        count === 1
          ? "Today's attendance is recorded."
          : "Attendance for all of today's sessions is recorded.",
      pending: checkIn.enrolmentStatus === BMT_ENROLMENT_STATUS.PENDING,
    };
  }
  if (result?.code === 'CHECKIN_NOT_OPEN') {
    const opens = formatTime(result.opensAt);
    return {
      kind: 'not-open',
      title: opens ? `Check-in opens at ${opens}` : "Check-in isn't open yet",
      message: opens ? `Nothing's saved yet. Scan again from ${opens}.` : "Nothing's saved yet.",
    };
  }
  if (result?.code === 'CHECKIN_CLOSED') {
    return {
      kind: 'closed',
      title: 'Check-in has closed',
      message: 'Please speak to the BMT team.',
    };
  }
  if (result?.code === 'SESSION_NOT_AVAILABLE' || result?.code === 'INVALID_QR') {
    return {
      kind: 'unavailable',
      title: "This session isn't open for check-in",
      message: "Check that you've scanned the correct QR code.",
    };
  }
  if (!result?.code && !result?.display) {
    // No answer at all: the network. The scan may still have saved, and a retry is safe.
    return {
      kind: 'offline',
      title: "Couldn't check you in",
      message: "Check your connection and try again. If you're already checked in, you'll see it.",
      retry: true,
    };
  }
  return {
    kind: 'error',
    title: "That didn't work",
    message: result?.display?.message || 'Please speak to the BMT team.',
  };
}

/** E4: someone the BMT team withdrew. Never a reason. */
export function withdrawnModel() {
  return {
    kind: 'withdrawn',
    title: 'Please speak to the BMT team',
    message: "Your BMT place isn't active right now.",
  };
}

const isEnrolled = me =>
  Boolean(me?.enrolment) && me.enrolment.status !== BMT_ENROLMENT_STATUS.WITHDRAWN;

/**
 * Which face the BMT hub shows: 'pending', 'approved', 'withdrawn' (the BMT team
 * took them off; they speak to the team, never re-sign-up here), 'signup' (not
 * enrolled, with an intake open), or 'none' (nothing to offer).
 */
export function bmtPhase(me) {
  if (!me) return 'none';
  if (isEnrolled(me)) {
    return me.enrolment.status === BMT_ENROLMENT_STATUS.APPROVED ? 'approved' : 'pending';
  }
  if (me.enrolment?.status === BMT_ENROLMENT_STATUS.WITHDRAWN) return 'withdrawn';
  return me.openIntakes?.length ? 'signup' : 'none';
}

/** 'For Youth & JC' under 'BMT Youth', from the course label. */
export const courseAudience = label => BMT_COURSE_AUDIENCE[label] || '';

/** The hero card on the hub: today's day, else the next to come; null once all are past. */
function heroDay(days, today) {
  return days.find(day => day.date === today) || days.find(day => day.date > today) || null;
}

/** 'attended' when any session that day was checked in to (one scan covers the day). */
const checkedInOn = day => (day?.sessions || []).some(s => s.status === 'attended');

/** The hero card's state: what the day is doing, from the trainee's side. */
function heroState(day, isToday) {
  if (day.online) return 'online';
  if (isToday && checkedInOn(day)) return 'checked-in';
  return day.checkin?.state || 'none';
}

/** The line under the hero card's date and time. */
function heroInfo(state, day, pending) {
  if (state === 'not-yet') {
    if (pending) {
      return `Going on ${formatDayDate(day.date)} anyway? Come along and scan the QR code at the venue to check in.`;
    }
    const opens = formatTime(day.checkin?.opensAt);
    return opens
      ? `Check-in opens at ${opens} on the day. Scan the QR code at the venue.`
      : 'Scan the QR code at the venue to check in.';
  }
  if (state === 'checked-in') {
    const count = day.sessions?.length || 0;
    return count === 1
      ? "Today's attendance is recorded."
      : `Attendance for all ${count} sessions today is recorded.`;
  }
  if (state === 'open') return pending ? 'You can check in while you wait to be confirmed.' : '';
  return (
    {
      closed: 'Check-in has closed. Please speak to the BMT team.',
      online: 'This session is on Zoom.',
    }[state] || ''
  );
}

const HERO_PILL = {
  'not-yet': { tone: 'muted', text: 'Check-in not open yet' },
  open: { tone: 'live', text: 'Check-in open' },
  'checked-in': { tone: 'success', text: 'Checked in' },
  closed: { tone: 'muted', text: 'Check-in closed' },
  online: { tone: 'muted', text: 'Zoom' },
  'dates-open': { tone: 'live', text: 'Dates open' },
};

/** The hero card for today's day, else the next to come. Pending sees no "not open" pill. */
function heroModel(day, isToday, pending) {
  const state = heroState(day, isToday);
  return {
    kind: 'day',
    label: isToday ? 'Today' : 'Next',
    date: formatDayDate(day.date),
    timeVenue: dayTimeVenue(day),
    state,
    pill: pending && state === 'not-yet' ? null : HERO_PILL[state] || null,
    info: heroInfo(state, day, pending),
    canScan: state === 'open',
    canPickMakeUp: false,
    meetingUrl: day.online && isToday ? day.meetingUrl || null : null,
  };
}

/** 'a', 'a and b', 'a, b and c'. */
export function joinWords(words = []) {
  const list = words.filter(Boolean);
  if (list.length <= 1) return list[0] || '';
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

/** 'Thu 5 Nov', 'Thu 5 Nov and Sun 13 Dec', 'A, B and C'. */
export const joinDates = (dates = []) => joinWords(dates.map(formatDayDate));

/** The make-ups still to do: every missed date with no make-up checked in yet. */
const makeUpsOf = me => (Array.isArray(me?.makeUps) ? me.makeUps : []);

/** The make-up booked for a missed date, as the day the trainee should turn up on. */
const bookedDays = me =>
  makeUpsOf(me)
    .filter(makeUp => makeUp.status === BMT_MAKE_UP_STATUS.BOOKED && makeUp.booked)
    .map(makeUp => makeUp.booked);

/**
 * When all of a trainee's own dates are behind them but a missed date is not
 * made up: MU4 (waiting for the next BMT) or MU5 (its dates are open, pick one).
 */
function makeUpHero(me) {
  const missed = makeUpsOf(me).filter(makeUp => makeUp.status !== BMT_MAKE_UP_STATUS.BOOKED);
  if (!missed.length) return null;
  const open = missed.some(makeUp => makeUp.status === BMT_MAKE_UP_STATUS.OPEN);
  return {
    kind: open ? 'make-up-open' : 'make-up-waiting',
    label: 'Next',
    date: open ? 'Make-up sessions' : `${joinDates(missed.map(m => m.missedDate))} sessions`,
    timeVenue: '',
    state: open ? 'dates-open' : 'none',
    pill: open ? HERO_PILL['dates-open'] : null,
    info: open
      ? 'The next BMT is open. Pick a date to make up what you missed.'
      : 'Make-up dates open with the next BMT.',
    canScan: false,
    canPickMakeUp: open,
    meetingUrl: null,
  };
}

/** What the "Previous session" row says under a missed date, about its make-up. */
function makeUpLine(me, date) {
  const makeUp = makeUpsOf(me).find(m => m.missedDate === date);
  if (!makeUp) return '';
  if (makeUp.status === BMT_MAKE_UP_STATUS.BOOKED && makeUp.booked) {
    return `Your make-up is on ${formatDayDate(makeUp.booked.date)}.`;
  }
  if (makeUp.status === BMT_MAKE_UP_STATUS.OPEN) {
    return 'The next BMT is open. Pick a date to make up what you missed.';
  }
  return 'You can make it up in the next BMT. We’ll let you know when dates open.';
}

const LOCKED_TILE_TEXT = 'Opens once the BMT team confirms your place';

/** Homework that counts towards "n of m done": everything but the private Reflection. */
const countedHomework = homework => (Array.isArray(homework) ? homework : []);

/** The three tiles. Pending trainees see Resources and Homework locked, saying when they open. */
function hubTiles(locked, todayResources, today, homework) {
  const counted = countedHomework(homework);
  const done = counted.filter(item => item.submitted).length;
  let resources = 'Videos for each session';
  if (todayResources) resources = `${formatDayDate(today)} resources are open`;
  return [
    { key: 'schedule', title: 'Schedule', subtitle: '', locked: false },
    {
      key: 'resources',
      title: 'Resources',
      subtitle: locked ? LOCKED_TILE_TEXT : resources,
      isNew: !locked && Boolean(todayResources),
      locked,
    },
    {
      key: 'tasks',
      title: 'Homework',
      subtitle: locked ? LOCKED_TILE_TEXT : homework ? `${done} of ${counted.length} done` : '',
      locked,
    },
  ];
}

/** Every date is attended when each of its sessions is (a make-up elsewhere counts). */
const dayAttended = day => dayStatus(day) === 'attended';

/**
 * The progress bar (Phase 3, PM screen 12): one segment per date of the course,
 * plus one for the Ministry Application, the only compulsory task. Green = done.
 * Missed dates stay grey; a booked make-up shows its own date instead.
 */
export function progressModel(me, homework = null) {
  const booked = Object.fromEntries(
    makeUpsOf(me)
      .filter(m => m.status === BMT_MAKE_UP_STATUS.BOOKED && m.booked)
      .map(m => [m.missedDate, m.booked.date]),
  );
  const segments = (me?.days || []).map(day => {
    const done = dayAttended(day);
    return {
      key: day.date,
      label: formatDayDate(!done && booked[day.date] ? booked[day.date] : day.date),
      done,
    };
  });
  const application = Array.isArray(homework)
    ? homework.find(item => item.kind === BMT_HOMEWORK_KIND.MINISTRY_APPLICATION)
    : null;
  // Pending trainees cannot read homework yet; the segment still shows, grey.
  if (application || !Array.isArray(homework)) {
    segments.push({
      key: 'ministry-application',
      label: 'Ministry Application',
      done: Boolean(application?.submitted),
    });
  }
  const done = segments.filter(segment => segment.done).length;
  const missedLeft = makeUpsOf(me).length;
  const othersDone = segments.length - done === missedLeft;
  return {
    done,
    total: segments.length,
    segments,
    remainingText:
      missedLeft && othersDone
        ? `${missedLeft} more ${missedLeft === 1 ? 'session' : 'sessions'} to complete BMT`
        : '',
  };
}

/**
 * Everything the BMT hub (L1–L7, MU0–MU5) shows for a pending or approved
 * trainee: the hero card, the previous session, the progress bar and the tiles.
 * `homework` is the /homework list once loaded (approved only), else null.
 */
export function hubModel(me, { homework = null, resources = null } = {}) {
  const phase = bmtPhase(me);
  const days = me?.days || [];
  const today = sgCalendarDate(me?.serverTime);
  const own = heroDay(days, today);
  const booked = heroDay(bookedDays(me), today);
  // A booked make-up is the next date to turn up to once it comes before the course's own.
  const day = booked && (!own || booked.date < own.date) ? booked : own;
  const past = days.filter(d => d.date < today && !d.online);
  const todayResources = (resources?.days || []).find(d => d.date === today && d.unlocked);
  const pending = phase === 'pending';
  const hero = day ? heroModel(day, day.date === today, pending) : makeUpHero(me);
  // Behind a make-up hero (MU3–MU5) the "Previous session" row would repeat it.
  const showLast = !hero || (day && day === own);
  const lastDay = showLast ? past[past.length - 1] : null;
  return {
    phase,
    label: me?.enrolment?.label || 'BMT',
    audience: courseAudience(me?.enrolment?.label),
    hero,
    allDone: !hero && days.length > 0,
    last: lastDay
      ? {
          date: formatDayDate(lastDay.date),
          attended: checkedInOn(lastDay),
          makeUp: checkedInOn(lastDay) ? '' : makeUpLine(me, lastDay.date),
        }
      : null,
    progress: progressModel(me, homework),
    tiles: hubTiles(phase !== 'approved', todayResources, today, homework),
  };
}

/**
 * The Schedule page: one card per date. Upcoming in-person dates say when
 * check-in opens; past dates and Zoom dates do not. No attendance here: the
 * hub already shows it.
 */
export function scheduleModel(me) {
  const today = sgCalendarDate(me?.serverTime);
  return (me?.days || []).map(day => ({
    date: day.date,
    title: formatDayDate(day.date),
    calendar: calendarParts(day.date),
    time: formatTimeRange(day.startsAt, day.endsAt),
    place: dayPlace(day),
    online: Boolean(day.online),
    meetingUrl: day.meetingUrl || null,
    past: day.date < today,
    checkinText:
      !day.online && day.checkin?.state === 'not-yet' && day.checkin.opensAt
        ? `Check-in opens ${formatTime(day.checkin.opensAt)}`
        : '',
    sessions: (day.sessions || []).map(session => ({
      id: session.id,
      title: session.title,
      note: session.note || '',
      start: formatTime(session.startsAt),
      end: formatTime(session.endsAt),
    })),
  }));
}

/** What a locked date on the Resources page says about when it opens. */
export function resourceLockText(day) {
  if (!day.online) {
    return 'You missed this date. Its videos open when you check in at your make-up.';
  }
  const at = formatTime(day.opensAt);
  return `Opens ${formatDayDate(day.date)}${at ? `, ${at}` : ''}`;
}

/**
 * The Resources page (library style, 3 Nov): one row of video cards per date,
 * scrolling sideways, then "Other resources" last. Everything is open from the
 * start except a date the trainee missed, until they check in at its make-up.
 */
export function resourcesLibraryModel(resources) {
  const rows = (resources?.days || []).map(day => {
    const title = [formatDayDate(day.date), day.online ? 'Zoom' : ''].filter(Boolean).join(' · ');
    if (!day.unlocked) {
      return { key: day.date, title, locked: true, lockText: resourceLockText(day), items: [] };
    }
    const items = (day.items || []).map(item => ({
      ...item,
      caption: item.session?.title && item.session.title !== item.title ? item.session.title : '',
      dateLabel: formatDayDate(item.session?.date || day.date),
    }));
    const videos = items.filter(item => item.kind === 'VIDEO').length;
    const count = videos === items.length ? videos : items.length;
    const noun = videos === items.length ? 'video' : 'item';
    return {
      key: day.date,
      title,
      countText: `${count} ${noun}${count === 1 ? '' : 's'}`,
      locked: false,
      lockText: '',
      items,
    };
  });
  return {
    rows: rows.filter(row => row.locked || row.items.length),
    other: resources?.general || [],
  };
}

const HOMEWORK_TITLE = {
  [BMT_HOMEWORK_KIND.MINISTRY_APPLICATION]: () => 'Submit Ministry Application',
  [BMT_HOMEWORK_KIND.TESTIMONY]: () => 'Write your Testimony',
  [BMT_HOMEWORK_KIND.VIDEO]: item => `Watch ${item.title}`,
};

const DONE_TAG = {
  [BMT_HOMEWORK_KIND.MINISTRY_APPLICATION]: 'Submitted',
  [BMT_HOMEWORK_KIND.TESTIMONY]: 'Submitted',
  [BMT_HOMEWORK_KIND.VIDEO]: 'Watched',
};

/** Items handed in against a date: the Ministry Application and the Testimony. */
const HAS_DUE_DATE = [BMT_HOMEWORK_KIND.MINISTRY_APPLICATION, BMT_HOMEWORK_KIND.TESTIMONY];

/** An item's kind, reading a missing one (older API) as GENERAL. */
export const homeworkKind = item => item?.kind || BMT_HOMEWORK_KIND.GENERAL;

/**
 * The Homework page (P2-05, 3 Nov): one row per item, its title the action, a
 * status tag under it ('Submitted', 'Watched', 'Draft saved') and, until done,
 * the due date. Due = the course's last date (the BMT team to confirm).
 * `draftIds` are the items with a draft kept on this device.
 */
export function homeworkModel(items = [], me = null, draftIds = []) {
  const days = me?.days || [];
  const lastDate = days.length ? days[days.length - 1].date : null;
  const rows = items.map(item => {
    const kind = homeworkKind(item);
    const done = Boolean(item.submitted);
    const draft = !done && draftIds.includes(item.id);
    const due =
      !done && lastDate && HAS_DUE_DATE.includes(kind) ? `Due ${formatDayDate(lastDate)}` : '';
    return {
      id: item.id,
      kind,
      title: (HOMEWORK_TITLE[kind] || (() => item.title))(item),
      done,
      draft,
      tags: [
        done ? { tone: 'success', text: DONE_TAG[kind] || 'Done' } : null,
        draft ? { tone: 'draft', text: 'Draft saved' } : null,
      ].filter(Boolean),
      due,
      item,
    };
  });
  return {
    rows,
    done: rows.filter(row => row.done).length,
    total: rows.length,
  };
}

/** 'Submitted on Tue 3 Nov' from a UTC instant, in Singapore time. */
export function submittedOn(value) {
  if (!value) return '';
  return `Submitted on ${formatDayDate(sgCalendarDate(value))}`;
}

/** 'Tue 3 Nov, 9.41am' from a UTC instant, in Singapore time. */
export function formatDateTime(value) {
  if (!value) return '';
  return `${formatDayDate(sgCalendarDate(value))}, ${formatTime(value)}`;
}

/** The dates of a run the trainee left unticked, as the sign-up's make-up hint. */
export function makeUpHint(days = [], ticked = [], label = 'BMT', { canChangeLater = true } = {}) {
  const missing = days.map(day => day.date).filter(date => !ticked.includes(date));
  if (!missing.length || missing.length === days.length) return '';
  const later = canChangeLater ? ' You can change your dates later.' : '';
  return `You'll need a make-up for ${joinDates(missing)} to complete ${label}.${later}`;
}

/** A row of "Dates attending" on the result screens: 'Sun 15 Nov', '5pm – 8.20pm', Zoom. */
const attendingRow = day => ({
  date: day.date,
  title: formatDayDate(day.date),
  time: formatTimeRange(day.startsAt, day.endsAt),
  online: Boolean(day.online),
});

/** The days the trainee said they will attend (every day when the API sends no list). */
export function attendingDays(me) {
  const days = me?.days || [];
  const dates = me?.enrolment?.attendingDates;
  if (!Array.isArray(dates)) return days;
  return days.filter(day => dates.includes(day.date));
}

/**
 * The result screens after sign-up (04a "You're in" / 04b "You're signed up"),
 * also after "Change my attendance" saves.
 */
export function signedUpModel(me) {
  const label = me?.enrolment?.label || 'BMT';
  const approved = me?.enrolment?.status === BMT_ENROLMENT_STATUS.APPROVED;
  const days = attendingDays(me);
  const first = days[0] || null;
  const firstVenue = days.find(day => !day.online) || null;
  const opens = formatTime(firstVenue?.checkin?.opensAt);
  const start = formatTime(first?.startsAt);
  let info = '';
  if (approved && firstVenue) {
    info = `On the day, scan the QR code at the venue to check in.${opens ? ` Check-in opens at ${opens}.` : ''}`;
  } else if (!approved && firstVenue) {
    info = `Going on ${formatDayDate(firstVenue.date)} anyway? Come along and scan the QR code at the venue to check in.`;
  }
  return {
    approved,
    label,
    title: approved ? `You're in for ${label}` : "You're signed up",
    subtitle: approved
      ? first
        ? `See you on ${formatDayDate(first.date)}${start ? ` at ${start}` : ''}`
        : ''
      : 'The team will confirm your sign up and be in touch once confirmed!',
    days: days.map(attendingRow),
    status: approved ? 'Confirmed' : 'Waiting to be confirmed',
    info,
    canChangeDates: me?.enrolment?.canChangeDates === true,
  };
}

/** The make-up picker (MU1) for one missed date. */
export function makeUpPickModel(makeUp) {
  if (!makeUp) return null;
  return {
    missedDate: makeUp.missedDate,
    title: `You missed ${formatDayDate(makeUp.missedDate)}`,
    booked: makeUp.booked ? `${makeUp.booked.intakeId}:${makeUp.booked.date}` : null,
    options: (makeUp.options || []).map(option => ({
      key: `${option.intakeId}:${option.date}`,
      intakeId: option.intakeId,
      date: option.date,
      title: formatDayDate(option.date),
      calendar: calendarParts(option.date),
      time: formatTimeRange(option.startsAt, option.endsAt),
      place: dayPlace(option),
      sessions: (option.sessions || []).map(session => session.title),
    })),
  };
}

/** "From i3 · Thu 5 Nov": which session brings a task in. */
export const taskSource = item =>
  item?.session
    ? `From ${[item.session.title, formatDayDate(item.session.date)].filter(Boolean).join(' · ')}`
    : '';

/** The earliest first date across the open intakes, for "BMT starts …". */
export function earliestStart(openIntakes = []) {
  const firsts = openIntakes.map(intake => intake.days?.[0]?.date).filter(Boolean);
  return firsts.length ? firsts.sort()[0] : null;
}

/**
 * The BMT tile in Home's Featured row (SU01 / SU01b), or null when it should not
 * render. While sign-ups are open it carries a "Sign-ups open" pill and when each
 * BMT starts; once someone is signed up it is the plain BMT key visual. Trainee
 * copy says "BMT" and never an intake or batch number.
 */
export function homeCardModel(me) {
  const phase = bmtPhase(me);
  if (phase === 'signup') {
    const lines = [...(me.openIntakes || [])]
      .map(intake => ({ label: intake.label, start: intake.days?.[0]?.date || '' }))
      .sort((a, b) => a.start.localeCompare(b.start))
      .map(({ label, start }) => {
        const name = label === 'BMT Youth' ? 'Youth' : label;
        return start ? `${name} from ${formatDayDate(start)}` : name;
      });
    return { phase, pill: 'Sign-ups open', lines };
  }
  if (phase === 'pending' || phase === 'approved') return { phase, pill: '', lines: [] };
  return null;
}

const ERROR_MESSAGES = {
  CG_NOTE_REQUIRED: 'Let us know whether you are in a Connect Group first.',
  INTAKE_NOT_OPEN: 'Sign-up for that BMT has just closed. The list now shows what is open.',
  ALREADY_ENROLLED: 'You are already signed up for BMT.',
  NOT_ENROLLED: 'Sign up for BMT to see your homework.',
  NOT_APPROVED: 'Homework opens once the BMT team confirms your place.',
  HOMEWORK_NOT_FOUND: 'That homework is no longer available.',
  TEXT_REQUIRED: 'Write your answer before submitting.',
  SECTIONS_REQUIRED: 'Fill in all 4 sections to submit. Your draft is saved.',
  NOT_SELF_REPORTED: 'This one ticks itself once your form is in.',
  DATES_REQUIRED: 'Tick at least one date.',
  INVALID_DATES: 'Those dates have changed. Please pick again.',
  DATES_LOCKED: 'BMT has started, so your dates can no longer change.',
  MAKEUP_NOT_AVAILABLE: 'That make-up date is no longer available. Please pick another.',
};

/** User-facing copy for an API error body `{ code, message }`, or a network failure. */
export function bmtErrorMessage(error) {
  const code = error?.code || error?.response?.data?.code;
  if (code && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];
  return 'Something went wrong. Please try again.';
}
